/** Pure derivation behind the workspace's WBS-leaf LEVEL pills. A WBS leaf
 *  carries a floor/level in its NAME ("… Top Floor", "… Level 2",
 *  "BlkR-Lower / Bottom Floor"); we map that to a canonical Takt_C ("LVLx")
 *  so the leaf can stamp its members' level at publish.
 *
 *  HARD RULE — degrade to null, never guess. A leaf whose name doesn't parse
 *  yields `null` ("set level" empty state); the user assigns it by hand. No
 *  React, no IO — mirrors boardLayout.ts. */
import type { ScopeMember } from "../../../models";

/** Floor/level words that map to a canonical level number. */
const LEVEL_WORD_TO_NUM: Array<{ re: RegExp; num: number }> = [
  // Top / Upper → 3 (the top floor in the renovation block's 3-floor stack).
  { re: /\b(top|upper)\b/i, num: 3 },
  { re: /\b(middle|mid)\b/i, num: 2 },
  { re: /\b(lower|bottom|ground)\b/i, num: 1 },
];

/** Derive a canonical Takt_C ("LVL3") from a WBS leaf's display name, or null
 *  when the name carries no recognizable floor/level token. */
export function deriveDefaultLevel(leafLabel: string | null | undefined): string | null {
  if (!leafLabel) return null;
  const label = leafLabel.trim();
  if (!label) return null;

  // Explicit "Level N" / "LVL N" / "Floor N" token — the most reliable signal.
  const explicit = label.match(/\b(?:lvl|level|floor|flr|fl)\s*\.?\s*([0-9]{1,2})\b/i);
  if (explicit) {
    const n = Number(explicit[1]);
    if (Number.isFinite(n) && n > 0) return `LVL${n}`;
  }

  // Floor WORDS (Top / Middle / Lower …) → a level number.
  for (const { re, num } of LEVEL_WORD_TO_NUM) {
    if (re.test(label)) return `LVL${num}`;
  }

  // Trailing bare number, but ONLY when the name actually mentions a floor or
  // level (so we don't read a block/zone index — "Block A.2" — as a level).
  const trailing = label.match(/([0-9]{1,2})\s*$/);
  if (trailing && /\b(?:floor|flr|fl|level|lvl)\b/i.test(label)) {
    const n = Number(trailing[1]);
    if (Number.isFinite(n) && n > 0) return `LVL${n}`;
  }

  return null;
}

/** A leaf's real Takt identity, read straight from its work. */
export interface LeafTakt {
  takt_a: string | null;
  takt_b: string | null;
  takt_c: string | null;
}

/** The MODAL (most-common) non-empty Takt_A/B/C across a leaf's members —
 *  preferring active work, falling back to completed when that's all there is.
 *  This is how a leaf knows what section/zone/level it actually IS, so the board
 *  can label and default it with zero clicks instead of guessing from the name. */
export function leafModalTakt(members: ScopeMember[]): LeafTakt {
  const modal = (pick: (m: ScopeMember) => string): string | null => {
    const tally = (list: ScopeMember[]): string | null => {
      const counts = new Map<string, number>();
      for (const m of list) {
        const v = (pick(m) || "").trim();
        if (!v) continue;
        counts.set(v, (counts.get(v) ?? 0) + 1);
      }
      let best: string | null = null;
      let bestN = 0;
      counts.forEach((n, v) => {
        if (n > bestN) {
          best = v;
          bestN = n;
        }
      });
      return best;
    };
    const active = members.filter((m) => m.status !== "TK_Complete");
    return tally(active) ?? tally(members);
  };
  return {
    takt_a: modal((m) => m.takt_a),
    takt_b: modal((m) => m.takt_b),
    takt_c: modal((m) => m.takt_c),
  };
}

/** Activity ids in a leaf whose NON-EMPTY takt_c disagrees with the leaf's
 *  effective level — the "coded X but filed as Y" reconcile set. Completed work
 *  (locked history) and members with a blank takt_c are ignored. When the leaf
 *  has no effective level (null), nothing is mis-coded. */
export function leafMiscode(
  members: ScopeMember[],
  effectiveLevel: string | null,
): number[] {
  if (!effectiveLevel) return [];
  const out: number[] = [];
  for (const m of members) {
    if (m.status === "TK_Complete") continue;
    const c = (m.takt_c || "").trim();
    if (!c) continue;
    if (c.toUpperCase() !== effectiveLevel.toUpperCase()) out.push(m.activity_id);
  }
  return out;
}

